import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { API, LOGO_URL } from "@/constants";
import { Plus, Pencil, Trash2, Archive, ArchiveRestore, LogOut, Mail, MailOpen, ChevronUp, ChevronDown, Upload, RefreshCw, FileDown, Eye, EyeOff } from "lucide-react";
import SmartImage from "@/components/SmartImage";

const downloadInvoice = async (order) => {
  try {
    const r = await axios.get(`${API}/trade/orders/${order.id}/invoice`, { withCredentials: true, responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Invoice-${order.order_no}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Could not download invoice");
  }
};

const exportOrdersCsv = async () => {
  try {
    const r = await axios.get(`${API}/admin/orders/export`, { withCredentials: true, responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "kera-orders.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Orders exported");
  } catch {
    toast.error("Could not export orders");
  }
};

const EMPTY_HUB = {
  country_name: "", country_code: "", city: "", region: "",
  latitude: "", longitude: "",
  hub_title: "", short_description: "", full_description: "",
  hub_image: "", partner_name: "",
  contact_email: "", contact_phone: "", website_url: "",
  journey_order: 0, show_on_map: true, featured: false, active: true,
};

const HUB_FIELDS = [
  ["country_name", "Country", "text", true],
  ["country_code", "Country Code", "text"],
  ["city", "City", "text"],
  ["region", "Region", "text"],
  ["latitude", "Latitude", "number", true],
  ["longitude", "Longitude", "number", true],
  ["hub_title", "Hub Title", "text"],
  ["short_description", "Short Description", "text"],
  ["partner_name", "Partner Name", "text"],
  ["contact_email", "Contact Email", "text"],
  ["contact_phone", "Contact Phone", "text"],
  ["website_url", "Website URL", "text"],
  ["journey_order", "Journey Order", "number"],
];

const EMPTY_PRODUCT = {
  name: "", category: "", subcategory: "", description: "", long_description: "", image: "",
  front_image: "", back_image: "", additional_images: "",
  product_code: "", barcode: "", net_weight: "", country_of_origin: "India",
  price: "", featured_image: "",
  ingredients: "", ingredients_raw: "", allergen_information: "", storage_information: "",
  pack_sizes: "", nutrition: "", cooking_instructions: "",
  nutrition_energy_kj: "", nutrition_energy_kcal: "", nutrition_fat: "", nutrition_saturates: "",
  nutrition_carbohydrates: "", nutrition_sugars: "", nutrition_fibre: "", nutrition_protein: "", nutrition_salt: "",
  sort_order: 0, stock_qty: "", low_stock_threshold: "", featured: false, new_product: false, active: true,
};

const EMPTY_VIDEO = {
  video_url: "", video_id: "", title: "", thumbnail: "",
  video_type: "standard", display_order: 0, active: true, featured: false, publish_date: "",
};

const EMPTY_RECIPE = {
  title: "", slug: "", description: "", image: "", product_ids: [],
  ingredients: "", instructions: "", prep_time: "", servings: "",
  display_order: 0, featured: false, active: true,
};

function Field({ label, children }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-kera-muted mb-1.5">{label}</label>
      {children}
    </div>
  );
}

const inputCls =
  "w-full rounded-lg border border-kera-line bg-white px-3.5 py-2 text-sm text-kera-ink outline-none focus:border-kera-gold";

const productToForm = (p) => ({
  ...EMPTY_PRODUCT, ...p,
  stock_qty: p.stock_qty ?? "",
  low_stock_threshold: p.low_stock_threshold ?? "",
  pack_sizes: (p.pack_sizes || []).join(", "),
  additional_images: (p.additional_images || []).join(", "),
  nutrition: (p.nutrition || []).map((n) => `${n.label} | ${n.value}`).join("\n"),
});

const EU_NUTRITION = [
  ["nutrition_energy_kj", "Energy (kJ)"],
  ["nutrition_energy_kcal", "Energy (kcal)"],
  ["nutrition_fat", "Fat (g)"],
  ["nutrition_saturates", "of which saturates (g)"],
  ["nutrition_carbohydrates", "Carbohydrate (g)"],
  ["nutrition_sugars", "of which sugars (g)"],
  ["nutrition_fibre", "Fibre (g)"],
  ["nutrition_protein", "Protein (g)"],
  ["nutrition_salt", "Salt (g)"],
];

const formToProduct = (f) => {
  const euFilled = EU_NUTRITION.some(([k]) => (f[k] || "").trim());
  const nutrition = euFilled
    ? EU_NUTRITION.filter(([k]) => (f[k] || "").trim()).map(([k, label]) => ({ label: label.replace(/ \((g|kJ|kcal)\)$/, "").replace(" (g)", ""), value: f[k].trim() }))
    : f.nutrition.split("\n").map((l) => l.split("|").map((s) => s.trim())).filter((a) => a[0]).map(([label, value]) => ({ label, value: value || "" }));
  return {
    name: f.name, category: f.category, subcategory: f.subcategory,
    description: f.description, long_description: f.long_description, image: f.image,
    front_image: f.front_image, back_image: f.back_image,
    additional_images: f.additional_images.split(",").map((s) => s.trim()).filter(Boolean),
    product_code: f.product_code, barcode: f.barcode, net_weight: f.net_weight,
    country_of_origin: f.country_of_origin, price: f.price, featured_image: f.featured_image,
    ingredients: f.ingredients, ingredients_raw: f.ingredients_raw,
    allergen_information: f.allergen_information, storage_information: f.storage_information,
    pack_sizes: f.pack_sizes.split(",").map((s) => s.trim()).filter(Boolean),
    nutrition,
    cooking_instructions: f.cooking_instructions,
    nutrition_energy_kj: f.nutrition_energy_kj, nutrition_energy_kcal: f.nutrition_energy_kcal,
    nutrition_fat: f.nutrition_fat, nutrition_saturates: f.nutrition_saturates,
    nutrition_carbohydrates: f.nutrition_carbohydrates, nutrition_sugars: f.nutrition_sugars,
    nutrition_fibre: f.nutrition_fibre, nutrition_protein: f.nutrition_protein, nutrition_salt: f.nutrition_salt,
    sort_order: parseInt(f.sort_order, 10) || 0,
    stock_qty: f.stock_qty === "" || f.stock_qty === null || f.stock_qty === undefined ? null : Math.max(0, parseInt(f.stock_qty, 10) || 0),
    low_stock_threshold: f.low_stock_threshold === "" || f.low_stock_threshold === null || f.low_stock_threshold === undefined ? null : Math.max(0, parseInt(f.low_stock_threshold, 10) || 0),
    featured: !!f.featured,
    new_product: !!f.new_product,
    active: !!f.active,
  };
};

function ImageUploadField({ label, value, onChange, testid }) {
  const [uploading, setUploading] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/upload`, fd, { withCredentials: true });
      onChange(r.data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Field label={label}>
      <div className="flex items-center gap-3 flex-wrap">
        {value && <img src={value} alt="" className="h-12 w-12 rounded-lg object-cover border border-kera-line" data-testid={`${testid}-preview`} />}
        <label className="inline-flex items-center gap-2 rounded-full border border-kera-line bg-white px-4 py-2 text-xs font-mono uppercase tracking-[0.15em] text-kera-muted hover:border-kera-gold hover:text-kera-copper transition-colors cursor-pointer" data-testid={`${testid}-upload`}>
          <Upload size={13} /> {uploading ? "Uploading…" : value ? "Replace" : "Upload"}
          <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={pick} />
        </label>
        <input value={value} onChange={(e) => onChange(e.target.value)} className={`${inputCls} flex-1 min-w-[200px]`} placeholder="or paste an image URL" data-testid={testid} />
      </div>
    </Field>
  );
}

function HubForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    onSave({
      ...form,
      latitude: parseFloat(form.latitude),
      longitude: parseFloat(form.longitude),
      journey_order: parseInt(form.journey_order, 10) || 0,
    });
  };

  return (
    <form onSubmit={submit} className="bg-white border border-kera-line rounded-2xl p-6 sm:p-8" data-testid="hub-form">
      <h3 className="font-display font-semibold text-xl text-kera-ink mb-6">{initial.id ? "Edit Hub" : "Add Hub"}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {HUB_FIELDS.map(([key, label, type, required]) => (
          <Field key={key} label={label}>
            <input
              type={type}
              step={type === "number" ? "any" : undefined}
              required={required}
              value={form[key]}
              onChange={(e) => set(key, e.target.value)}
              className={inputCls}
              data-testid={`hub-field-${key.replace(/_/g, "-")}`}
            />
          </Field>
        ))}
        <div className="sm:col-span-2">
          <ImageUploadField label="Hub Image" value={form.hub_image} onChange={(v) => set("hub_image", v)} testid="hub-field-image" />
        </div>
        <div className="sm:col-span-2">
          <Field label="Full Description">
            <textarea rows={3} value={form.full_description} onChange={(e) => set("full_description", e.target.value)} className={inputCls} data-testid="hub-field-full-description" />
          </Field>
        </div>
      </div>
      <div className="mt-5 flex flex-wrap gap-6">
        {[["show_on_map", "Show on Map"], ["featured", "Featured"], ["active", "Active"]].map(([key, label]) => (
          <label key={key} className="flex items-center gap-2 text-sm text-kera-muted cursor-pointer">
            <input type="checkbox" checked={!!form[key]} onChange={(e) => set(key, e.target.checked)} className="accent-[#C5A059] h-4 w-4" data-testid={`hub-toggle-${key.replace(/_/g, "-")}`} />
            {label}
          </label>
        ))}
      </div>
      <div className="mt-7 flex gap-3">
        <button type="submit" className="rounded-full bg-black text-white px-6 py-2.5 text-sm hover:bg-kera-gold hover:text-black transition-colors" data-testid="hub-form-save">Save Hub</button>
        <button type="button" onClick={onCancel} className="rounded-full border border-kera-line px-6 py-2.5 text-sm text-kera-muted hover:border-kera-copper transition-colors" data-testid="hub-form-cancel">Cancel</button>
      </div>
    </form>
  );
}

function ProductForm({ initial, categories, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  const [lowConf, setLowConf] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [extracted, setExtracted] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const fieldCls = (k) => (lowConf.includes(k) ? `${inputCls} border-amber-400 ring-1 ring-amber-300 bg-amber-50/50` : inputCls);

  const extract = async () => {
    if (!form.front_image && !form.back_image) {
      toast.error("Upload a front or back package image first");
      return;
    }
    setExtracting(true);
    try {
      const r = await axios.post(
        `${API}/admin/products/extract`,
        { front_image: form.front_image, back_image: form.back_image },
        { withCredentials: true, timeout: 180000 }
      );
      const fields = r.data.fields || {};
      const confidence = r.data.confidence || {};
      const mapped = {};
      Object.entries(fields).forEach(([k, v]) => {
        if (v) mapped[k] = v;
      });
      if (mapped.ingredients_raw && !form.ingredients) mapped.ingredients = mapped.ingredients_raw;
      setForm((f) => ({ ...f, ...mapped }));
      setLowConf(Object.keys(mapped).filter((k) => confidence[k] && confidence[k] !== "high"));
      setExtracted(true);
      toast.success("Extraction complete — review highlighted fields, then save to publish");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setExtracting(false);
    }
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(formToProduct(form)); }} className="bg-white border border-kera-line rounded-2xl p-6 sm:p-8" data-testid="product-form">
      <h3 className="font-display font-semibold text-xl text-kera-ink mb-6">{initial.id ? "Edit Product" : "Add Product"}</h3>

      {/* Package images + AI extraction */}
      <div className="rounded-xl border border-kera-gold/40 bg-[#FAF7F0] p-5 mb-8" data-testid="ai-extract-section">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-kera-copper mb-4">Package Images &amp; AI Label Extraction</p>
        <div className="grid grid-cols-1 gap-4">
          <ImageUploadField label="Front Package Image" value={form.front_image} onChange={(v) => set("front_image", v)} testid="product-field-front-image" />
          <ImageUploadField label="Back Package Image" value={form.back_image} onChange={(v) => set("back_image", v)} testid="product-field-back-image" />
        </div>
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={extract}
            disabled={extracting}
            className="rounded-full bg-kera-gold text-black px-6 py-2.5 text-sm font-semibold hover:bg-black hover:text-kera-gold transition-colors disabled:opacity-50"
            data-testid="extract-button"
          >
            {extracting ? "Analysing label…" : extracted ? "Re-analyse Images" : "Extract Product Information"}
          </button>
          {extracted && (
            <span className="text-xs text-amber-700" data-testid="extract-review-note">
              Draft only — highlighted fields are low-confidence. Nothing is published until you click Save Product.
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Name">
          <input required value={form.name} onChange={(e) => set("name", e.target.value)} className={fieldCls("name")} data-testid="product-field-name" />
        </Field>
        <Field label="Category">
          <select required value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls} data-testid="product-field-category">
            <option value="">Select…</option>
            {categories.map((c) => <option key={c.id || c.name} value={c.name}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Subcategory">
          <input value={form.subcategory} onChange={(e) => set("subcategory", e.target.value)} className={inputCls} data-testid="product-field-subcategory" />
        </Field>
        <Field label="Product Code">
          <input value={form.product_code} onChange={(e) => set("product_code", e.target.value)} className={fieldCls("product_code")} data-testid="product-field-code" />
        </Field>
        <Field label="Barcode">
          <input value={form.barcode} onChange={(e) => set("barcode", e.target.value)} className={fieldCls("barcode")} data-testid="product-field-barcode" />
        </Field>
        <Field label="Net Weight">
          <input value={form.net_weight} onChange={(e) => set("net_weight", e.target.value)} className={fieldCls("net_weight")} placeholder="400 g" data-testid="product-field-net-weight" />
        </Field>
        <Field label="Country of Origin">
          <input value={form.country_of_origin} onChange={(e) => set("country_of_origin", e.target.value)} className={fieldCls("country_of_origin")} data-testid="product-field-origin" />
        </Field>
        <Field label="Wholesale Price (€ — visible to approved traders only)">
          <input value={form.price} onChange={(e) => set("price", e.target.value)} className={inputCls} placeholder="12.50" data-testid="product-field-price" />
        </Field>
        <Field label="Stock Quantity (blank = untracked)">
          <input type="number" min="0" value={form.stock_qty} onChange={(e) => set("stock_qty", e.target.value)} className={inputCls} placeholder="e.g. 120" data-testid="product-field-stock" />
        </Field>
        <Field label="Low Stock Alert At (blank = no alert)">
          <input type="number" min="0" value={form.low_stock_threshold} onChange={(e) => set("low_stock_threshold", e.target.value)} className={inputCls} placeholder="e.g. 10" data-testid="product-field-low-stock" />
        </Field>
        <Field label="Sort Order">
          <input type="number" value={form.sort_order} onChange={(e) => set("sort_order", e.target.value)} className={inputCls} data-testid="product-field-sort-order" />
        </Field>
        <div className="sm:col-span-2">
          <ImageUploadField label="Shop Image (shown in catalog)" value={form.image} onChange={(v) => set("image", v)} testid="product-field-image" />
        </div>
        <div className="sm:col-span-2">
          <ImageUploadField label="Featured Range Thumbnail (homepage teaser override)" value={form.featured_image} onChange={(v) => set("featured_image", v)} testid="product-field-featured-image" />
        </div>
        <div className="sm:col-span-2">
          <Field label="Short Description">
            <input value={form.description} onChange={(e) => set("description", e.target.value)} className={inputCls} data-testid="product-field-description" />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Full Description">
            <textarea rows={2} value={form.long_description} onChange={(e) => set("long_description", e.target.value)} className={inputCls} data-testid="product-field-long-description" />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Ingredients — Display (comma separated)">
            <textarea rows={2} value={form.ingredients} onChange={(e) => set("ingredients", e.target.value)} className={inputCls} data-testid="product-field-ingredients" placeholder="Urad dal, onion, green chilli…" />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Ingredients — Raw (as printed on label)">
            <textarea rows={2} value={form.ingredients_raw} onChange={(e) => set("ingredients_raw", e.target.value)} className={fieldCls("ingredients_raw")} data-testid="product-field-ingredients-raw" />
          </Field>
        </div>
        <Field label="Allergen Information">
          <textarea rows={2} value={form.allergen_information} onChange={(e) => set("allergen_information", e.target.value)} className={fieldCls("allergen_information")} data-testid="product-field-allergens" />
        </Field>
        <Field label="Storage Information">
          <textarea rows={2} value={form.storage_information} onChange={(e) => set("storage_information", e.target.value)} className={fieldCls("storage_information")} data-testid="product-field-storage" />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Cooking Instructions (one step per line)">
            <textarea rows={3} value={form.cooking_instructions} onChange={(e) => set("cooking_instructions", e.target.value)} className={fieldCls("cooking_instructions")} data-testid="product-field-cooking" />
          </Field>
        </div>
        <Field label="Pack Sizes (comma separated)">
          <input value={form.pack_sizes} onChange={(e) => set("pack_sizes", e.target.value)} className={inputCls} data-testid="product-field-pack-sizes" placeholder="400g · 12 pieces, 2kg · foodservice" />
        </Field>
        <Field label="Additional Images (comma separated URLs)">
          <input value={form.additional_images} onChange={(e) => set("additional_images", e.target.value)} className={inputCls} data-testid="product-field-additional-images" />
        </Field>
      </div>

      {/* EU nutrition */}
      <div className="mt-6 rounded-xl border border-kera-line p-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-kera-copper mb-4">Nutrition · per 100g (EU)</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {EU_NUTRITION.map(([key, label]) => (
            <Field key={key} label={label}>
              <input value={form[key]} onChange={(e) => set(key, e.target.value)} className={fieldCls(key)} data-testid={`product-field-${key.replace(/_/g, "-")}`} />
            </Field>
          ))}
        </div>
        <Field label="Legacy Nutrition (used only if EU fields are empty — one per line: Label | Value)">
          <textarea rows={3} value={form.nutrition} onChange={(e) => set("nutrition", e.target.value)} className={`${inputCls} mt-3`} data-testid="product-field-nutrition" />
        </Field>
      </div>

      <div className="mt-5 flex flex-wrap gap-6">
        <label className="flex items-center gap-2 text-sm text-kera-muted cursor-pointer">
          <input type="checkbox" checked={!!form.active} onChange={(e) => set("active", e.target.checked)} className="accent-[#C5A059] h-4 w-4" data-testid="product-toggle-active" />
          Active (visible in the shop)
        </label>
        <label className="flex items-center gap-2 text-sm text-kera-muted cursor-pointer">
          <input type="checkbox" checked={!!form.featured} onChange={(e) => set("featured", e.target.checked)} className="accent-[#C5A059] h-4 w-4" data-testid="product-toggle-featured" />
          Featured (stars on the homepage — first 4 shown)
        </label>
        <label className="flex items-center gap-2 text-sm text-kera-muted cursor-pointer">
          <input type="checkbox" checked={!!form.new_product} onChange={(e) => set("new_product", e.target.checked)} className="accent-[#C5A059] h-4 w-4" data-testid="product-toggle-new" />
          New Product (shows NEW badge)
        </label>
      </div>
      <div className="mt-7 flex gap-3">
        <button type="submit" className="rounded-full bg-black text-white px-6 py-2.5 text-sm hover:bg-kera-gold hover:text-black transition-colors" data-testid="product-form-save">Confirm &amp; Save</button>
        <button type="button" onClick={onCancel} className="rounded-full border border-kera-line px-6 py-2.5 text-sm text-kera-muted hover:border-kera-copper transition-colors" data-testid="product-form-cancel">Cancel</button>
      </div>
    </form>
  );
}

function VideoForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  const [looking, setLooking] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const lookup = async () => {
    if (!form.video_url) return;
    setLooking(true);
    try {
      const r = await axios.post(`${API}/admin/videos/lookup`, { url: form.video_url }, { withCredentials: true });
      setForm((f) => ({
        ...f,
        video_id: r.data.video_id,
        title: f.title || r.data.title,
        thumbnail: r.data.thumbnail,
        video_type: r.data.suggested_type,
      }));
      toast.success("Video details fetched from YouTube");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLooking(false);
    }
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave({ ...form, display_order: parseInt(form.display_order, 10) || 0 }); }} className="bg-white border border-kera-line rounded-2xl p-6 sm:p-8" data-testid="video-form">
      <h3 className="font-display font-semibold text-xl text-kera-ink mb-6">{initial.id ? "Edit Video" : "Add Video"}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <Field label="YouTube URL">
            <div className="flex gap-2">
              <input value={form.video_url} onChange={(e) => set("video_url", e.target.value)} className={`${inputCls} flex-1`} placeholder="https://www.youtube.com/watch?v=… or youtu.be/… or /shorts/…" data-testid="video-field-url" />
              <button type="button" onClick={lookup} disabled={looking} className="shrink-0 rounded-full border border-kera-gold/60 px-4 py-2 text-xs font-mono uppercase tracking-[0.12em] text-kera-copper hover:bg-kera-gold hover:text-black transition-colors disabled:opacity-50" data-testid="video-fetch-button">
                {looking ? "Fetching…" : "Fetch Details"}
              </button>
            </div>
          </Field>
        </div>
        <Field label="Title">
          <input required value={form.title} onChange={(e) => set("title", e.target.value)} className={inputCls} data-testid="video-field-title" />
        </Field>
        <Field label="Video Type">
          <select value={form.video_type} onChange={(e) => set("video_type", e.target.value)} className={inputCls} data-testid="video-field-type">
            <option value="standard">Standard (16:9 — Watch KERA)</option>
            <option value="story">Story / Reel (9:16 — KERA Stories)</option>
          </select>
        </Field>
        <Field label="Thumbnail URL">
          <input value={form.thumbnail} onChange={(e) => set("thumbnail", e.target.value)} className={inputCls} placeholder="auto-filled from YouTube" data-testid="video-field-thumbnail" />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Display Order">
            <input type="number" value={form.display_order} onChange={(e) => set("display_order", e.target.value)} className={inputCls} data-testid="video-field-order" />
          </Field>
          <Field label="Publish Date">
            <input value={form.publish_date} onChange={(e) => set("publish_date", e.target.value)} className={inputCls} placeholder="2026-08-10" data-testid="video-field-date" />
          </Field>
        </div>
      </div>
      {form.thumbnail && <img src={form.thumbnail} alt="" className="mt-4 h-24 rounded-lg border border-kera-line object-cover" data-testid="video-thumb-preview" />}
      <div className="mt-5 flex flex-wrap gap-6">
        <label className="flex items-center gap-2 text-sm text-kera-muted cursor-pointer">
          <input type="checkbox" checked={!!form.active} onChange={(e) => set("active", e.target.checked)} className="accent-[#C5A059] h-4 w-4" data-testid="video-toggle-active" />
          Active (visible on site)
        </label>
        <label className="flex items-center gap-2 text-sm text-kera-muted cursor-pointer">
          <input type="checkbox" checked={!!form.featured} onChange={(e) => set("featured", e.target.checked)} className="accent-[#C5A059] h-4 w-4" data-testid="video-toggle-featured" />
          Featured
        </label>
      </div>
      <div className="mt-7 flex gap-3">
        <button type="submit" className="rounded-full bg-black text-white px-6 py-2.5 text-sm hover:bg-kera-gold hover:text-black transition-colors" data-testid="video-form-save">Save Video</button>
        <button type="button" onClick={onCancel} className="rounded-full border border-kera-line px-6 py-2.5 text-sm text-kera-muted hover:border-kera-copper transition-colors" data-testid="video-form-cancel">Cancel</button>
      </div>
    </form>
  );
}

function RecipeForm({ initial, products, onSave, onCancel }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleProduct = (id) =>
    setForm((f) => ({
      ...f,
      product_ids: f.product_ids.includes(id) ? f.product_ids.filter((p) => p !== id) : [...f.product_ids, id],
    }));

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave({ ...form, display_order: parseInt(form.display_order, 10) || 0 }); }} className="bg-white border border-kera-line rounded-2xl p-6 sm:p-8" data-testid="recipe-form">
      <h3 className="font-display font-semibold text-xl text-kera-ink mb-6">{initial.id ? "Edit Recipe" : "Add Recipe"}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Title">
          <input required value={form.title} onChange={(e) => set("title", e.target.value)} className={inputCls} data-testid="recipe-field-title" />
        </Field>
        <Field label="Display Order">
          <input type="number" value={form.display_order} onChange={(e) => set("display_order", e.target.value)} className={inputCls} data-testid="recipe-field-order" />
        </Field>
        <div className="sm:col-span-2">
          <ImageUploadField label="Recipe Image" value={form.image} onChange={(v) => set("image", v)} testid="recipe-field-image" />
        </div>
        <div className="sm:col-span-2">
          <Field label="Description">
            <textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} className={inputCls} data-testid="recipe-field-description" />
          </Field>
        </div>
        <Field label="Prep Time">
          <input value={form.prep_time} onChange={(e) => set("prep_time", e.target.value)} className={inputCls} placeholder="15 mins" data-testid="recipe-field-prep-time" />
        </Field>
        <Field label="Servings">
          <input value={form.servings} onChange={(e) => set("servings", e.target.value)} className={inputCls} placeholder="2–3" data-testid="recipe-field-servings" />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Ingredients (one per line)">
            <textarea rows={4} value={form.ingredients} onChange={(e) => set("ingredients", e.target.value)} className={inputCls} data-testid="recipe-field-ingredients" />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Method (one step per line)">
            <textarea rows={4} value={form.instructions} onChange={(e) => set("instructions", e.target.value)} className={inputCls} data-testid="recipe-field-instructions" />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-kera-muted mb-2">Made with KERA products</p>
          <div className="flex flex-wrap gap-2" data-testid="recipe-products-picker">
            {products.map((p) => (
              <button
                type="button"
                key={p.id}
                onClick={() => toggleProduct(p.id)}
                data-testid={`recipe-product-toggle-${p.id}`}
                className={`rounded-full px-4 py-1.5 text-xs border transition-colors ${
                  form.product_ids.includes(p.id)
                    ? "bg-black text-kera-gold border-black"
                    : "border-kera-line text-kera-muted hover:border-kera-gold"
                }`}
              >
                {p.name}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-5 flex flex-wrap gap-6">
        <label className="flex items-center gap-2 text-sm text-kera-muted cursor-pointer">
          <input type="checkbox" checked={!!form.active} onChange={(e) => set("active", e.target.checked)} className="accent-[#C5A059] h-4 w-4" data-testid="recipe-toggle-active" />
          Active (visible on site)
        </label>
        <label className="flex items-center gap-2 text-sm text-kera-muted cursor-pointer">
          <input type="checkbox" checked={!!form.featured} onChange={(e) => set("featured", e.target.checked)} className="accent-[#C5A059] h-4 w-4" data-testid="recipe-toggle-featured" />
          Featured
        </label>
      </div>
      <div className="mt-7 flex gap-3">
        <button type="submit" className="rounded-full bg-black text-white px-6 py-2.5 text-sm hover:bg-kera-gold hover:text-black transition-colors" data-testid="recipe-form-save">Save Recipe</button>
        <button type="button" onClick={onCancel} className="rounded-full border border-kera-line px-6 py-2.5 text-sm text-kera-muted hover:border-kera-copper transition-colors" data-testid="recipe-form-cancel">Cancel</button>
      </div>
    </form>
  );
}

function OrderCard({ order, onUpdate }) {
  const [status, setStatus] = useState(order.status);
  const [noteText, setNoteText] = useState("");
  const STATUSES = ["pending", "confirmed", "packed", "shipped", "delivered", "cancelled"];
  const STATUS_STYLE = {
    pending: "bg-kera-line/60 text-kera-muted",
    confirmed: "bg-kera-gold/20 text-kera-copper",
    packed: "bg-kera-gold/20 text-kera-copper",
    shipped: "bg-black text-kera-gold",
    delivered: "bg-black text-white",
    cancelled: "bg-red-100 text-red-700",
  };
  return (
    <div className="bg-white border border-kera-line rounded-2xl p-6" data-testid={`admin-order-card-${order.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-display font-semibold text-lg text-kera-ink">{order.order_no}</p>
          <p className="text-sm text-kera-muted mt-0.5">
            {order.trader_company || order.trader_name} · {order.trader_email}
          </p>
          <p className="text-xs text-kera-muted mt-0.5">
            {order.created_at ? new Date(order.created_at).toLocaleString() : ""} · Deliver to: {order.branch_name} ({order.branch_address})
          </p>
          {order.delivery_day && <p className="text-xs text-kera-copper mt-0.5" data-testid={`order-delivery-day-${order.id}`}>Preferred delivery day: {order.delivery_day}</p>}
          {order.note && <p className="text-xs text-kera-muted mt-1">Note: {order.note}</p>}
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => downloadInvoice(order)} className="inline-flex items-center gap-1.5 rounded-full border border-kera-line px-4 py-1.5 text-[10px] font-mono uppercase tracking-[0.15em] text-kera-muted hover:border-kera-copper hover:text-kera-copper transition-colors" data-testid={`admin-order-invoice-${order.id}`}>
            <FileDown size={12} /> Invoice
          </button>
          <span className={`rounded-full px-4 py-1.5 text-[10px] font-mono uppercase tracking-[0.15em] ${STATUS_STYLE[order.status]}`} data-testid={`admin-order-status-${order.id}`}>
            {order.status}
          </span>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {order.items.map((i, idx) => (
          <span key={idx} className="rounded-full bg-[#FAF7F0] border border-kera-line px-3 py-1 text-xs text-kera-ink">
            {i.name} × {i.qty} — €{i.line_total?.toFixed(2)}
          </span>
        ))}
      </div>
      <p className="mt-3 text-sm font-semibold text-kera-ink">Total: €{order.total?.toFixed(2)}</p>
      <div className="mt-4 pt-4 border-t border-kera-line/70 flex flex-wrap items-center gap-3">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-full border border-kera-line bg-white px-4 py-2 text-xs font-mono uppercase tracking-[0.12em] outline-none focus:border-kera-gold" data-testid={`order-status-select-${order.id}`}>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Optional note for trader" className="flex-1 min-w-[180px] rounded-full border border-kera-line bg-white px-4 py-2 text-xs outline-none focus:border-kera-gold" data-testid={`order-note-${order.id}`} />
        <button
          onClick={() => onUpdate(order, status, noteText)}
          disabled={status === order.status && !noteText}
          className="rounded-full bg-black text-white px-5 py-2 text-xs hover:bg-kera-gold hover:text-black transition-colors disabled:opacity-40"
          data-testid={`order-update-${order.id}`}
        >
          Update & Notify
        </button>
      </div>
      {order.status_history?.length > 0 && (
        <ol className="mt-4 space-y-1.5">
          {order.status_history.map((h, i) => (
            <li key={i} className="flex items-center gap-3 text-xs text-kera-muted">
              <span className="h-1.5 w-1.5 rounded-full bg-kera-gold shrink-0" />
              <span className="capitalize">{h.status}</span>
              {h.note && <span>— {h.note}</span>}
              <span className="ml-auto">{h.at ? new Date(h.at).toLocaleString() : ""}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [hubs, setHubs] = useState([]);
  const [traders, setTraders] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [team, setTeam] = useState([]);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "manager" });
  const [editingHub, setEditingHub] = useState(null);
  const [settings, setSettings] = useState(null);
  const [siteContent, setSiteContent] = useState(null);
  const [homeLayout, setHomeLayout] = useState([]);
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({ name: "", order: 0 });
  const [enquiries, setEnquiries] = useState([]);
  const [videos, setVideos] = useState([]);
  const [editingVideo, setEditingVideo] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [editingRecipe, setEditingRecipe] = useState(null);

  const authed = { withCredentials: true };
  const loadHubs = () => axios.get(`${API}/admin/hubs`, authed).then((r) => setHubs(r.data.hubs)).catch(() => {});
  const loadProducts = () => axios.get(`${API}/admin/products`, authed).then((r) => setProducts(r.data.products)).catch(() => {});
  const loadCategories = () => axios.get(`${API}/admin/categories`, authed).then((r) => setCategories(r.data.categories)).catch(() => {});
  const loadEnquiries = () => axios.get(`${API}/admin/enquiries`, authed).then((r) => setEnquiries(r.data.enquiries)).catch(() => {});
  const loadVideos = () => axios.get(`${API}/admin/videos`, authed).then((r) => setVideos(r.data.videos)).catch(() => {});
  const loadRecipes = () => axios.get(`${API}/admin/recipes`, authed).then((r) => setRecipes(r.data.recipes)).catch(() => {});
  const loadTraders = () => axios.get(`${API}/admin/traders`, authed).then((r) => setTraders(r.data.traders)).catch(() => {});
  const loadOrders = () => axios.get(`${API}/admin/orders`, authed).then((r) => setOrders(r.data.orders)).catch(() => {});
  const loadStats = () => axios.get(`${API}/admin/stats`, authed).then((r) => setStats(r.data)).catch(() => {});
  const loadTeam = () => axios.get(`${API}/admin/users`, authed).then((r) => setTeam(r.data.users)).catch(() => {});

  useEffect(() => {
    loadHubs();
    loadProducts();
    loadCategories();
    loadEnquiries();
    loadVideos();
    loadRecipes();
    loadTraders();
    loadOrders();
    loadStats();
    loadTeam();
    axios.get(`${API}/journey/settings`, authed).then((r) => setSettings(r.data)).catch(() => {});
    axios.get(`${API}/site/content`, authed).then((r) => setSiteContent(r.data)).catch(() => {});
    axios.get(`${API}/homepage/layout`, authed).then((r) => setHomeLayout(r.data.sections || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fail = (err) => toast.error(formatApiErrorDetail(err.response?.data?.detail));

  const saveHub = async (data) => {
    try {
      if (editingHub.id) {
        await axios.put(`${API}/admin/hubs/${editingHub.id}`, data, authed);
        toast.success("Hub updated — the map now reflects it.");
      } else {
        await axios.post(`${API}/admin/hubs`, data, authed);
        toast.success("Hub added — it now appears on the journey map.");
      }
      setEditingHub(null);
      loadHubs();
    } catch (err) { fail(err); }
  };

  const toggleArchive = async (hub) => {
    try {
      await axios.put(`${API}/admin/hubs/${hub.id}`, { ...hub, active: !hub.active }, authed);
      toast.success(hub.active ? "Hub archived" : "Hub restored");
      loadHubs();
    } catch (err) { fail(err); }
  };

  const deleteHub = async (hub) => {
    if (!window.confirm(`Delete ${hub.country_name} hub permanently?`)) return;
    try {
      await axios.delete(`${API}/admin/hubs/${hub.id}`, authed);
      toast.success("Hub deleted");
      loadHubs();
    } catch (err) { fail(err); }
  };

  const saveProduct = async (data) => {
    try {
      if (editingProduct.id) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, data, authed);
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/admin/products`, data, authed);
        toast.success("Product added to the range");
      }
      setEditingProduct(null);
      loadProducts();
    } catch (err) { fail(err); }
  };

  const toggleProductActive = async (p) => {
    try {
      await axios.put(`${API}/admin/products/${p.id}`, formToProduct(productToForm({ ...p, active: !p.active })), authed);
      toast.success(p.active ? "Product hidden from shop" : "Product live in shop");
      loadProducts();
    } catch (err) { fail(err); }
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(`Delete ${p.name} permanently?`)) return;
    try {
      await axios.delete(`${API}/admin/products/${p.id}`, authed);
      toast.success("Product deleted");
      loadProducts();
    } catch (err) { fail(err); }
  };

  const saveCategory = async (data, id) => {
    try {
      if (id) {
        await axios.put(`${API}/admin/categories/${id}`, data, authed);
        toast.success("Category updated");
      } else {
        await axios.post(`${API}/admin/categories`, data, authed);
        toast.success("Category added");
      }
      setEditingCategory(null);
      setNewCategory({ name: "", order: 0 });
      loadCategories();
    } catch (err) { fail(err); }
  };

  const deleteCategory = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"? Products keep their current category label.`)) return;
    try {
      await axios.delete(`${API}/admin/categories/${c.id}`, authed);
      toast.success("Category deleted");
      loadCategories();
    } catch (err) { fail(err); }
  };

  const markEnquiry = async (enq, read) => {
    try {
      await axios.put(`${API}/admin/enquiries/${enq.id}`, { read }, authed);
      loadEnquiries();
    } catch (err) { fail(err); }
  };

  const deleteEnquiry = async (enq) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try {
      await axios.delete(`${API}/admin/enquiries/${enq.id}`, authed);
      toast.success("Enquiry deleted");
      loadEnquiries();
    } catch (err) { fail(err); }
  };

  const saveVideo = async (data) => {
    try {
      if (editingVideo.id) {
        await axios.put(`${API}/admin/videos/${editingVideo.id}`, data, authed);
        toast.success("Video updated");
      } else {
        await axios.post(`${API}/admin/videos`, data, authed);
        toast.success("Video added to the site");
      }
      setEditingVideo(null);
      loadVideos();
    } catch (err) { fail(err); }
  };

  const toggleVideoActive = async (v) => {
    try {
      await axios.put(`${API}/admin/videos/${v.id}`, { ...v, active: !v.active }, authed);
      toast.success(v.active ? "Video disabled" : "Video enabled");
      loadVideos();
    } catch (err) { fail(err); }
  };

  const deleteVideo = async (v) => {
    if (!window.confirm(`Delete "${v.title}"?`)) return;
    try {
      await axios.delete(`${API}/admin/videos/${v.id}`, authed);
      toast.success("Video deleted");
      loadVideos();
    } catch (err) { fail(err); }
  };

  const moveVideo = async (video, dir) => {
    const sorted = [...videos].sort((a, b) => a.display_order - b.display_order);
    const idx = sorted.findIndex((v) => v.id === video.id);
    const swap = sorted[idx + dir];
    if (!swap) return;
    try {
      await axios.put(`${API}/admin/videos/${video.id}`, { ...video, display_order: swap.display_order }, authed);
      await axios.put(`${API}/admin/videos/${swap.id}`, { ...swap, display_order: video.display_order }, authed);
      loadVideos();
    } catch (err) { fail(err); }
  };

  const syncChannel = async () => {
    try {
      const r = await axios.post(`${API}/admin/videos/sync`, {}, authed);
      toast.success(`Channel synced — ${r.data.added} new video${r.data.added === 1 ? "" : "s"} added`);
      loadVideos();
    } catch (err) { fail(err); }
  };

  const saveRecipe = async (data) => {
    try {
      if (editingRecipe.id) {
        await axios.put(`${API}/admin/recipes/${editingRecipe.id}`, data, authed);
        toast.success("Recipe updated");
      } else {
        await axios.post(`${API}/admin/recipes`, data, authed);
        toast.success("Recipe added");
      }
      setEditingRecipe(null);
      loadRecipes();
    } catch (err) { fail(err); }
  };

  const toggleRecipeActive = async (r) => {
    try {
      await axios.put(`${API}/admin/recipes/${r.id}`, { ...r, active: !r.active }, authed);
      toast.success(r.active ? "Recipe hidden" : "Recipe live");
      loadRecipes();
    } catch (err) { fail(err); }
  };

  const deleteRecipe = async (r) => {
    if (!window.confirm(`Delete "${r.title}"?`)) return;
    try {
      await axios.delete(`${API}/admin/recipes/${r.id}`, authed);
      toast.success("Recipe deleted");
      loadRecipes();
    } catch (err) { fail(err); }
  };

  const setTraderStatus = async (t, status) => {
    try {
      await axios.put(`${API}/admin/traders/${t.id}/status`, { status }, authed);
      toast.success(`${t.name} ${status}`);
      loadTraders();
      loadStats();
    } catch (err) { fail(err); }
  };

  const updateOrderStatus = async (order, status, noteText) => {
    try {
      await axios.put(`${API}/admin/orders/${order.id}/status`, { status, note: noteText || "" }, authed);
      toast.success(`Order ${order.order_no} → ${status}`);
      loadOrders();
      loadStats();
    } catch (err) { fail(err); }
  };

  const createUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/users`, newUser, authed);
      toast.success(`${newUser.role} account created for ${newUser.email}`);
      setNewUser({ name: "", email: "", password: "", role: "manager" });
      loadTeam();
    } catch (err) { fail(err); }
  };

  const saveSiteContent = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/site/content`, siteContent, authed);
      toast.success("Homepage images updated");
    } catch (err) { fail(err); }
  };

  const moveSection = (idx, dir) =>
    setHomeLayout((l) => {
      const next = [...l];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return l;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });

  const toggleSection = (idx) =>
    setHomeLayout((l) => l.map((s, i) => (i === idx ? { ...s, visible: !s.visible } : s)));

  const saveHomeLayout = async () => {
    try {
      await axios.put(`${API}/admin/homepage/layout`, {
        sections: homeLayout.map(({ key, visible }) => ({ key, visible })),
      }, authed);
      toast.success("Homepage layout saved");
    } catch (err) { fail(err); }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/journey/settings`, {
        ...settings,
        origin_latitude: parseFloat(settings.origin_latitude),
        origin_longitude: parseFloat(settings.origin_longitude),
      }, authed);
      toast.success("Journey settings saved");
    } catch (err) { fail(err); }
  };

  const unread = enquiries.filter((e) => !e.read).length;
  const pendingTraders = traders.filter((t) => t.status === "pending").length;
  const openOrders = orders.filter((o) => !["delivered", "cancelled"].includes(o.status)).length;
  const isManager = user?.role === "manager";
  const TABS = [
    ["dashboard", "Dashboard"],
    ...(isManager ? [] : [["hubs", "Food Hubs"]]),
    ["products", "Products"],
    ...(isManager ? [] : [["categories", "Categories"], ["videos", "Videos"], ["recipes", "Recipes"]]),
    ["traders", `Traders${pendingTraders ? ` (${pendingTraders})` : ""}`],
    ["orders", `Orders${openOrders ? ` (${openOrders})` : ""}`],
    ["enquiries", `Enquiries${unread ? ` (${unread})` : ""}`],
    ...(isManager ? [] : [["homepage", "Homepage"], ["journey", "Journey Settings"]]),
  ];

  return (
    <div className="min-h-screen bg-[#FAF7F0]" data-testid="admin-dashboard">
      <header className="border-b border-kera-line bg-white/85 backdrop-blur-md sticky top-0 z-40">
        <div className="px-4 sm:px-8 max-w-6xl mx-auto h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/"><img src={LOGO_URL} alt="KERA" className="h-8 w-auto" /></Link>
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-kera-copper">Brand Studio</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-xs text-kera-muted" data-testid="admin-user-email">{user?.email}</span>
            <button onClick={logout} className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.15em] text-kera-muted hover:text-kera-copper transition-colors" data-testid="admin-logout-button">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="px-4 sm:px-8 max-w-6xl mx-auto py-10">
        <div className="flex gap-2 mb-8 overflow-x-auto pb-1" data-testid="admin-tabs">
          {TABS.map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`admin-tab-${key}`}
              className={`whitespace-nowrap rounded-full px-5 py-2 text-xs font-mono uppercase tracking-[0.15em] border transition-colors ${
                tab === key ? "bg-black text-white border-black" : "border-kera-line text-kera-muted hover:border-kera-copper bg-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "dashboard" && (
          <div data-testid="admin-dashboard-panel">
            {stats && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-10" data-testid="stats-grid">
                {[
                  ["Active Products", stats.products], ["Open Orders", stats.orders_open],
                  ["Pending Traders", stats.pending_traders], ["Approved Traders", stats.approved_traders],
                  ["Unread Enquiries", stats.unread_enquiries],
                ].map(([label, val]) => (
                  <div key={label} className="bg-white border border-kera-line rounded-2xl p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                    <p className="font-display font-bold text-3xl text-kera-ink">{val}</p>
                    <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.18em] text-kera-muted">{label}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div className="bg-white border border-kera-line rounded-2xl p-5 text-kera-muted">
                Catalogue: <span className="text-kera-ink font-medium">{stats?.hubs} hubs</span> · <span className="text-kera-ink font-medium">{stats?.videos} videos</span> · <span className="text-kera-ink font-medium">{stats?.recipes} recipes</span>
              </div>
              <div className="bg-white border border-kera-line rounded-2xl p-5 text-kera-muted">
                Orders all-time: <span className="text-kera-ink font-medium">{stats?.orders_total}</span>
              </div>
            </div>

            {user?.role === "admin" && (
              <div className="mt-10 max-w-2xl" data-testid="team-section">
                <h3 className="font-display font-semibold text-xl text-kera-ink mb-2">Team Accounts</h3>
                <p className="text-sm text-kera-muted mb-5">Managers can add products and approve traders — nothing else.</p>
                <div className="bg-white border border-kera-line rounded-2xl divide-y divide-kera-line/70 mb-5">
                  {team.map((u) => (
                    <div key={u.id} className="flex items-center gap-4 px-5 py-3.5" data-testid={`team-row-${u.id}`}>
                      <span className="flex-1 text-sm text-kera-ink font-medium">{u.name} <span className="text-kera-muted font-normal">· {u.email}</span></span>
                      <span className="rounded-full bg-black text-kera-gold px-3 py-1 text-[10px] font-mono uppercase tracking-[0.15em]">{u.role}</span>
                    </div>
                  ))}
                </div>
                <form onSubmit={createUser} className="bg-white border border-kera-line rounded-2xl p-5 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="create-user-form">
                  <input required placeholder="Name" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} className={inputCls} data-testid="user-field-name" />
                  <input required type="email" placeholder="Email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className={inputCls} data-testid="user-field-email" />
                  <input required type="password" minLength={8} placeholder="Password (min 8)" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} className={inputCls} data-testid="user-field-password" />
                  <select value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })} className={inputCls} data-testid="user-field-role">
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </select>
                  <button type="submit" className="sm:col-span-2 rounded-full bg-black text-white py-2.5 text-sm hover:bg-kera-gold hover:text-black transition-colors" data-testid="user-create-button">
                    Create Team Account
                  </button>
                </form>
              </div>
            )}
          </div>
        )}

        {tab === "traders" && (
          <div data-testid="admin-traders-panel">
            <p className="text-sm text-kera-muted mb-6">{traders.length} trader application{traders.length === 1 ? "" : "s"}. Approving emails the trader automatically.</p>
            {traders.length === 0 ? (
              <div className="text-center py-16 border border-dashed border-kera-line rounded-2xl bg-white">
                <p className="font-display font-semibold text-xl text-kera-ink">No trader applications yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {traders.map((t) => (
                  <div key={t.id} className={`bg-white border rounded-2xl p-6 ${t.status === "pending" ? "border-kera-gold/70" : "border-kera-line"}`} data-testid={`trader-card-${t.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-display font-semibold text-lg text-kera-ink">
                          {t.name} {t.company && <span className="text-kera-muted font-normal">· {t.company}</span>}
                        </p>
                        <p className="text-sm text-kera-muted mt-1">
                          {t.email} {t.phone && `· ${t.phone}`} {t.country && `· ${t.country}`}
                        </p>
                        <p className="text-xs text-kera-muted mt-1">
                          {[t.business_type, t.vat_id && `VAT: ${t.vat_id}`, t.address, t.postcode].filter(Boolean).join(" · ")}
                        </p>
                        {t.message && <p className="mt-2 text-sm text-kera-muted">{t.message}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`rounded-full px-3 py-1 text-[10px] font-mono uppercase tracking-[0.15em] ${
                          t.status === "approved" ? "bg-black text-kera-gold" : t.status === "rejected" ? "bg-red-100 text-red-700" : "bg-kera-gold/20 text-kera-copper"
                        }`} data-testid={`trader-status-${t.id}`}>
                          {t.status}
                        </span>
                        {t.status !== "approved" && (
                          <button onClick={() => setTraderStatus(t, "approved")} className="rounded-full bg-black text-white px-4 py-1.5 text-xs hover:bg-kera-gold hover:text-black transition-colors" data-testid={`trader-approve-${t.id}`}>
                            Approve
                          </button>
                        )}
                        {t.status !== "rejected" && (
                          <button onClick={() => setTraderStatus(t, "rejected")} className="rounded-full border border-kera-line px-4 py-1.5 text-xs text-kera-muted hover:border-red-300 hover:text-red-700 transition-colors" data-testid={`trader-reject-${t.id}`}>
                            Reject
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "orders" && (
          <div data-testid="admin-orders-panel">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              <p className="text-sm text-kera-muted">{orders.length} order{orders.length === 1 ? "" : "s"}. Updating a status emails the trader automatically.</p>
              {orders.length > 0 && (
                <button onClick={exportOrdersCsv} className="inline-flex items-center gap-2 rounded-full bg-black text-white px-5 py-2.5 text-xs font-mono uppercase tracking-[0.15em] hover:bg-kera-gold hover:text-black transition-colors" data-testid="orders-export-csv">
                  <FileDown size={14} /> Export CSV
                </button>
              )}
            </div>
            {orders.length === 0 ? (
              <div className="text-center py-16 border border-dashed border-kera-line rounded-2xl bg-white">
                <p className="font-display font-semibold text-xl text-kera-ink">No orders yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((o) => (
                  <OrderCard key={o.id} order={o} onUpdate={updateOrderStatus} />
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "hubs" && (
          <div data-testid="admin-hubs-panel">
            {editingHub ? (
              <HubForm initial={editingHub} onSave={saveHub} onCancel={() => setEditingHub(null)} />
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <p className="text-sm text-kera-muted">{hubs.length} hub{hubs.length === 1 ? "" : "s"} — additions appear on the public map automatically.</p>
                  <button onClick={() => setEditingHub({ ...EMPTY_HUB })} className="inline-flex items-center gap-2 rounded-full bg-black text-white px-5 py-2.5 text-sm hover:bg-kera-gold hover:text-black transition-colors" data-testid="add-hub-button">
                    <Plus size={15} /> Add Hub
                  </button>
                </div>
                <div className="bg-white border border-kera-line rounded-2xl overflow-x-auto">
                  <table className="w-full text-sm min-w-[720px]" data-testid="hubs-table">
                    <thead>
                      <tr className="border-b border-kera-line text-left">
                        {["Order", "Country", "City", "Title", "On Map", "Status", ""].map((h) => (
                          <th key={h} className="px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.2em] text-kera-muted">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {hubs.map((hub) => (
                        <tr key={hub.id} className="border-b border-kera-line/60 last:border-0" data-testid={`hub-row-${hub.id}`}>
                          <td className="px-5 py-4 font-mono text-kera-copper">{hub.journey_order}</td>
                          <td className="px-5 py-4 text-kera-ink font-medium">{hub.country_name}</td>
                          <td className="px-5 py-4 text-kera-muted">{hub.city}</td>
                          <td className="px-5 py-4 text-kera-muted">{hub.hub_title}</td>
                          <td className="px-5 py-4 text-kera-muted">{hub.show_on_map ? "Yes" : "No"}</td>
                          <td className="px-5 py-4">
                            <span className={`rounded-full px-3 py-1 text-[10px] font-mono uppercase tracking-[0.15em] ${hub.active ? "bg-kera-gold/20 text-kera-copper" : "bg-kera-line/60 text-kera-muted"}`}>
                              {hub.active ? "Active" : "Archived"}
                            </span>
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex gap-2 justify-end">
                              <button onClick={() => setEditingHub({ ...hub })} className="p-2 text-kera-muted hover:text-kera-copper transition-colors" title="Edit" data-testid={`hub-edit-${hub.id}`}><Pencil size={15} /></button>
                              <button onClick={() => toggleArchive(hub)} className="p-2 text-kera-muted hover:text-kera-copper transition-colors" title={hub.active ? "Archive" : "Restore"} data-testid={`hub-archive-${hub.id}`}>
                                {hub.active ? <Archive size={15} /> : <ArchiveRestore size={15} />}
                              </button>
                              <button onClick={() => deleteHub(hub)} className="p-2 text-kera-muted hover:text-red-700 transition-colors" title="Delete" data-testid={`hub-delete-${hub.id}`}><Trash2 size={15} /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {tab === "products" && (
          <div data-testid="admin-products-panel">
            {editingProduct ? (
              <ProductForm initial={editingProduct} categories={categories} onSave={saveProduct} onCancel={() => setEditingProduct(null)} />
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <p className="text-sm text-kera-muted">{products.length} product{products.length === 1 ? "" : "s"} — lower sort order shows first; the first 4 featured products star on the homepage.</p>
                  <button onClick={() => setEditingProduct({ ...EMPTY_PRODUCT })} className="inline-flex items-center gap-2 rounded-full bg-black text-white px-5 py-2.5 text-sm hover:bg-kera-gold hover:text-black transition-colors" data-testid="add-product-button">
                    <Plus size={15} /> Add Product
                  </button>
                </div>
                <div className="bg-white border border-kera-line rounded-2xl overflow-x-auto">
                  <table className="w-full text-sm min-w-[720px]" data-testid="products-table">
                    <thead>
                      <tr className="border-b border-kera-line text-left">
                        {["", "Order", "Name", "Category", "Featured", "Status", ""].map((h) => (
                          <th key={h} className="px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.2em] text-kera-muted">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {products.map((p) => (
                        <tr key={p.id} className="border-b border-kera-line/60 last:border-0" data-testid={`product-row-${p.id}`}>
                          <td className="px-5 py-3">
                            {p.image && <SmartImage src={p.image} alt="" sizes="40px" className="h-10 w-10 rounded-lg object-cover border border-kera-line" />}
                          </td>
                          <td className="px-5 py-4 font-mono text-kera-copper">{p.sort_order}</td>
                          <td className="px-5 py-4 text-kera-ink font-medium">{p.name}</td>
                          <td className="px-5 py-4 text-kera-muted">{p.category}</td>
                          <td className="px-5 py-4 text-kera-gold font-semibold">{p.featured ? "★" : ""}</td>
                          <td className="px-5 py-4">
                            <span className={`rounded-full px-3 py-1 text-[10px] font-mono uppercase tracking-[0.15em] ${p.active ? "bg-kera-gold/20 text-kera-copper" : "bg-kera-line/60 text-kera-muted"}`}>
                              {p.active ? "Live" : "Hidden"}
                            </span>
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex gap-2 justify-end">
                              <button onClick={() => setEditingProduct(productToForm(p))} className="p-2 text-kera-muted hover:text-kera-copper transition-colors" title="Edit" data-testid={`product-edit-${p.id}`}><Pencil size={15} /></button>
                              <button onClick={() => toggleProductActive(p)} className="p-2 text-kera-muted hover:text-kera-copper transition-colors" title={p.active ? "Hide" : "Publish"} data-testid={`product-toggle-${p.id}`}>
                                {p.active ? <Archive size={15} /> : <ArchiveRestore size={15} />}
                              </button>
                              <button onClick={() => deleteProduct(p)} className="p-2 text-kera-muted hover:text-red-700 transition-colors" title="Delete" data-testid={`product-delete-${p.id}`}><Trash2 size={15} /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {tab === "videos" && (
          <div data-testid="admin-videos-panel">
            {editingVideo ? (
              <VideoForm initial={editingVideo} onSave={saveVideo} onCancel={() => setEditingVideo(null)} />
            ) : (
              <>
                <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
                  <p className="text-sm text-kera-muted">{videos.length} video{videos.length === 1 ? "" : "s"} — paste any YouTube URL and details auto-fill. Shorts are suggested as Stories.</p>
                  <div className="flex gap-2">
                    <button onClick={syncChannel} className="inline-flex items-center gap-2 rounded-full border border-kera-line bg-white px-5 py-2.5 text-sm text-kera-muted hover:border-kera-copper transition-colors" data-testid="sync-channel-button" title="Requires YOUTUBE_API_KEY">
                      <RefreshCw size={14} /> Sync Channel
                    </button>
                    <button onClick={() => setEditingVideo({ ...EMPTY_VIDEO, display_order: (videos[videos.length - 1]?.display_order || 0) + 1 })} className="inline-flex items-center gap-2 rounded-full bg-black text-white px-5 py-2.5 text-sm hover:bg-kera-gold hover:text-black transition-colors" data-testid="add-video-button">
                      <Plus size={15} /> Add Video
                    </button>
                  </div>
                </div>
                <div className="bg-white border border-kera-line rounded-2xl overflow-x-auto">
                  <table className="w-full text-sm min-w-[760px]" data-testid="videos-table">
                    <thead>
                      <tr className="border-b border-kera-line text-left">
                        {["Order", "", "Title", "Type", "Status", ""].map((h) => (
                          <th key={h} className="px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.2em] text-kera-muted">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[...videos].sort((a, b) => a.display_order - b.display_order).map((v, idx, arr) => (
                        <tr key={v.id} className="border-b border-kera-line/60 last:border-0" data-testid={`video-row-${v.id}`}>
                          <td className="px-5 py-3">
                            <div className="flex flex-col">
                              <button onClick={() => moveVideo(v, -1)} disabled={idx === 0} className="text-kera-muted hover:text-kera-copper disabled:opacity-25" data-testid={`video-up-${v.id}`}><ChevronUp size={14} /></button>
                              <button onClick={() => moveVideo(v, 1)} disabled={idx === arr.length - 1} className="text-kera-muted hover:text-kera-copper disabled:opacity-25" data-testid={`video-down-${v.id}`}><ChevronDown size={14} /></button>
                            </div>
                          </td>
                          <td className="px-5 py-3">
                            {v.thumbnail && <img src={v.thumbnail} alt="" className="h-10 w-16 rounded object-cover border border-kera-line" />}
                          </td>
                          <td className="px-5 py-4 text-kera-ink font-medium max-w-[280px] truncate">
                            {v.title} {v.featured && <span className="text-kera-gold">★</span>}
                          </td>
                          <td className="px-5 py-4">
                            <span className={`rounded-full px-3 py-1 text-[10px] font-mono uppercase tracking-[0.15em] ${v.video_type === "story" ? "bg-black text-kera-gold" : "bg-kera-line/60 text-kera-muted"}`}>
                              {v.video_type === "story" ? "Story" : "Standard"}
                            </span>
                          </td>
                          <td className="px-5 py-4">
                            <span className={`rounded-full px-3 py-1 text-[10px] font-mono uppercase tracking-[0.15em] ${v.active ? "bg-kera-gold/20 text-kera-copper" : "bg-kera-line/60 text-kera-muted"}`}>
                              {v.active ? "Live" : "Off"}
                            </span>
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex gap-2 justify-end">
                              <button onClick={() => setEditingVideo({ ...v })} className="p-2 text-kera-muted hover:text-kera-copper transition-colors" title="Edit" data-testid={`video-edit-${v.id}`}><Pencil size={15} /></button>
                              <button onClick={() => toggleVideoActive(v)} className="p-2 text-kera-muted hover:text-kera-copper transition-colors" title={v.active ? "Disable" : "Enable"} data-testid={`video-toggle-${v.id}`}>
                                {v.active ? <Archive size={15} /> : <ArchiveRestore size={15} />}
                              </button>
                              <button onClick={() => deleteVideo(v)} className="p-2 text-kera-muted hover:text-red-700 transition-colors" title="Delete" data-testid={`video-delete-${v.id}`}><Trash2 size={15} /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {tab === "categories" && (
          <div className="max-w-2xl" data-testid="admin-categories-panel">
            <p className="text-sm text-kera-muted mb-6">Categories appear as filter chips on the shop page, in this order.</p>
            <form
              onSubmit={(e) => { e.preventDefault(); saveCategory({ name: newCategory.name, order: parseInt(newCategory.order, 10) || 0 }); }}
              className="bg-white border border-kera-line rounded-2xl p-5 flex flex-wrap gap-3 items-end mb-6"
              data-testid="category-add-form"
            >
              <div className="flex-1 min-w-[180px]">
                <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-kera-muted mb-1.5">New Category</label>
                <input required value={newCategory.name} onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })} className={inputCls} data-testid="category-field-name" />
              </div>
              <div className="w-24">
                <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-kera-muted mb-1.5">Order</label>
                <input type="number" value={newCategory.order} onChange={(e) => setNewCategory({ ...newCategory, order: e.target.value })} className={inputCls} data-testid="category-field-order" />
              </div>
              <button type="submit" className="rounded-full bg-black text-white px-5 py-2 text-sm hover:bg-kera-gold hover:text-black transition-colors" data-testid="category-add-button">Add</button>
            </form>
            <div className="bg-white border border-kera-line rounded-2xl divide-y divide-kera-line/70" data-testid="categories-list">
              {categories.map((c) => (
                <div key={c.id} className="flex items-center gap-4 px-5 py-4" data-testid={`category-row-${c.id}`}>
                  {editingCategory?.id === c.id ? (
                    <div className="flex-1 space-y-4">
                      <div className="flex items-center gap-4">
                        <input value={editingCategory.name} onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })} className={`${inputCls} flex-1`} data-testid="category-edit-name" />
                        <input type="number" value={editingCategory.order} onChange={(e) => setEditingCategory({ ...editingCategory, order: e.target.value })} className={`${inputCls} w-20`} data-testid="category-edit-order" />
                        <button onClick={() => saveCategory({ name: editingCategory.name, order: parseInt(editingCategory.order, 10) || 0, image: editingCategory.image || "" }, c.id)} className="text-xs font-mono uppercase tracking-[0.15em] text-kera-copper hover:text-kera-ink" data-testid="category-save-button">Save</button>
                        <button onClick={() => setEditingCategory(null)} className="text-xs font-mono uppercase tracking-[0.15em] text-kera-muted">Cancel</button>
                      </div>
                      <ImageUploadField label="Cover Photo (homepage category card — blank = use a product photo)" value={editingCategory.image || ""} onChange={(v) => setEditingCategory({ ...editingCategory, image: v })} testid="category-edit-image" />
                    </div>
                  ) : (
                    <>
                      <span className="font-mono text-kera-copper w-8">{c.order}</span>
                      {c.image ? <img src={c.image} alt="" className="h-10 w-10 rounded-lg object-cover border border-kera-line" /> : null}
                      <span className="flex-1 text-kera-ink font-medium">{c.name}</span>
                      <button onClick={() => setEditingCategory({ ...c })} className="p-2 text-kera-muted hover:text-kera-copper transition-colors" data-testid={`category-edit-${c.id}`}><Pencil size={15} /></button>
                      <button onClick={() => deleteCategory(c)} className="p-2 text-kera-muted hover:text-red-700 transition-colors" data-testid={`category-delete-${c.id}`}><Trash2 size={15} /></button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "recipes" && (
          <div data-testid="admin-recipes-panel">
            {editingRecipe ? (
              <RecipeForm initial={editingRecipe} products={products} onSave={saveRecipe} onCancel={() => setEditingRecipe(null)} />
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <p className="text-sm text-kera-muted">{recipes.length} recipe{recipes.length === 1 ? "" : "s"} — link KERA products so readers can shop each dish.</p>
                  <button onClick={() => setEditingRecipe({ ...EMPTY_RECIPE, display_order: (recipes[recipes.length - 1]?.display_order || 0) + 1 })} className="inline-flex items-center gap-2 rounded-full bg-black text-white px-5 py-2.5 text-sm hover:bg-kera-gold hover:text-black transition-colors" data-testid="add-recipe-button">
                    <Plus size={15} /> Add Recipe
                  </button>
                </div>
                <div className="bg-white border border-kera-line rounded-2xl overflow-x-auto">
                  <table className="w-full text-sm min-w-[680px]" data-testid="recipes-table">
                    <thead>
                      <tr className="border-b border-kera-line text-left">
                        {["", "Order", "Title", "Products", "Status", ""].map((h) => (
                          <th key={h} className="px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.2em] text-kera-muted">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[...recipes].sort((a, b) => a.display_order - b.display_order).map((r) => (
                        <tr key={r.id} className="border-b border-kera-line/60 last:border-0" data-testid={`recipe-row-${r.id}`}>
                          <td className="px-5 py-3">
                            {r.image && <img src={r.image} alt="" className="h-10 w-10 rounded-lg object-cover border border-kera-line" />}
                          </td>
                          <td className="px-5 py-4 font-mono text-kera-copper">{r.display_order}</td>
                          <td className="px-5 py-4 text-kera-ink font-medium">
                            {r.title} {r.featured && <span className="text-kera-gold">★</span>}
                          </td>
                          <td className="px-5 py-4 text-kera-muted">{r.product_ids?.length || 0} linked</td>
                          <td className="px-5 py-4">
                            <span className={`rounded-full px-3 py-1 text-[10px] font-mono uppercase tracking-[0.15em] ${r.active ? "bg-kera-gold/20 text-kera-copper" : "bg-kera-line/60 text-kera-muted"}`}>
                              {r.active ? "Live" : "Hidden"}
                            </span>
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex gap-2 justify-end">
                              <button onClick={() => setEditingRecipe({ ...r })} className="p-2 text-kera-muted hover:text-kera-copper transition-colors" title="Edit" data-testid={`recipe-edit-${r.id}`}><Pencil size={15} /></button>
                              <button onClick={() => toggleRecipeActive(r)} className="p-2 text-kera-muted hover:text-kera-copper transition-colors" title={r.active ? "Hide" : "Publish"} data-testid={`recipe-toggle-${r.id}`}>
                                {r.active ? <Archive size={15} /> : <ArchiveRestore size={15} />}
                              </button>
                              <button onClick={() => deleteRecipe(r)} className="p-2 text-kera-muted hover:text-red-700 transition-colors" title="Delete" data-testid={`recipe-delete-${r.id}`}><Trash2 size={15} /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {tab === "enquiries" && (
          <div data-testid="admin-enquiries-panel">
            <p className="text-sm text-kera-muted mb-6">{enquiries.length} message{enquiries.length === 1 ? "" : "s"} — wholesale enquiries and trader applications.</p>
            {enquiries.length === 0 ? (
              <div className="text-center py-16 border border-dashed border-kera-line rounded-2xl bg-white">
                <p className="font-display font-semibold text-xl text-kera-ink">No enquiries yet</p>
                <p className="mt-2 text-sm text-kera-muted">Wholesale requests and trader applications will appear here.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {enquiries.map((enq) => (
                  <div key={enq.id} className={`bg-white border rounded-2xl p-6 ${enq.read ? "border-kera-line" : "border-kera-gold/70"}`} data-testid={`enquiry-card-${enq.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-display font-semibold text-lg text-kera-ink">
                          {enq.name} {enq.company && <span className="text-kera-muted font-normal">· {enq.company}</span>}
                          {enq.type === "trader" && (
                            <span className="ml-2 align-middle rounded-full bg-black text-kera-gold px-3 py-0.5 text-[10px] font-mono uppercase tracking-[0.15em]">Trader</span>
                          )}
                        </p>
                        <p className="text-sm text-kera-muted mt-1">
                          {enq.email} {enq.phone && `· ${enq.phone}`} {enq.country && `· ${enq.country}`}
                        </p>
                        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-kera-copper mt-1">
                          {enq.created_at ? new Date(enq.created_at).toLocaleString() : ""} {enq.read ? "· Read" : "· New"}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => markEnquiry(enq, !enq.read)} className="p-2 text-kera-muted hover:text-kera-copper transition-colors" title={enq.read ? "Mark unread" : "Mark read"} data-testid={`enquiry-read-${enq.id}`}>
                          {enq.read ? <Mail size={15} /> : <MailOpen size={15} />}
                        </button>
                        <button onClick={() => deleteEnquiry(enq)} className="p-2 text-kera-muted hover:text-red-700 transition-colors" data-testid={`enquiry-delete-${enq.id}`}><Trash2 size={15} /></button>
                      </div>
                    </div>
                    {enq.items?.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {enq.items.map((item, i) => (
                          <span key={i} className="rounded-full bg-kera-gold/15 border border-kera-gold/40 px-3 py-1 text-xs text-kera-ink">
                            KERA {item.name}
                          </span>
                        ))}
                      </div>
                    )}
                    {enq.message && <p className="mt-4 text-sm text-kera-muted leading-relaxed border-t border-kera-line/70 pt-4">{enq.message}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "homepage" && siteContent && (
          <form onSubmit={saveSiteContent} className="bg-white border border-kera-line rounded-2xl p-6 sm:p-8 max-w-2xl" data-testid="admin-homepage-form">
            <h3 className="font-display font-semibold text-xl text-kera-ink mb-2">Homepage Images</h3>
            <p className="text-sm text-kera-muted mb-6">Upload replacements — the homepage updates as soon as you save.</p>
            <div className="space-y-5">
              <ImageUploadField label="Hero Background Image" value={siteContent.hero_image} onChange={(v) => setSiteContent({ ...siteContent, hero_image: v })} testid="site-field-hero-image" />
              <ImageUploadField label="Story Section Image (large)" value={siteContent.story_image} onChange={(v) => setSiteContent({ ...siteContent, story_image: v })} testid="site-field-story-image" />
              <ImageUploadField label="Story Section Image (small overlay)" value={siteContent.story_image_secondary} onChange={(v) => setSiteContent({ ...siteContent, story_image_secondary: v })} testid="site-field-story-image-secondary" />
            </div>
            <button type="submit" className="mt-7 rounded-full bg-black text-white px-6 py-2.5 text-sm hover:bg-kera-gold hover:text-black transition-colors" data-testid="site-save-button">
              Save Images
            </button>
          </form>
        )}

        {tab === "homepage" && homeLayout.length > 0 && (
          <div className="bg-white border border-kera-line rounded-2xl p-6 sm:p-8 max-w-2xl mt-6" data-testid="homepage-layout-panel">
            <h3 className="font-display font-semibold text-xl text-kera-ink mb-1">Homepage Sections</h3>
            <p className="text-sm text-kera-muted mb-6">Reorder or hide sections on the home page. Hero, marquee and footer always show.</p>
            <div className="divide-y divide-kera-line/70 border border-kera-line rounded-xl overflow-hidden">
              {homeLayout.map((s, i) => (
                <div key={s.key} className={`flex items-center gap-3 px-5 py-4 ${s.visible ? "" : "opacity-50"}`} data-testid={`layout-row-${s.key}`}>
                  <span className="flex-1 text-sm font-medium text-kera-ink">{s.label}</span>
                  {!s.visible && <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-red-600">Hidden</span>}
                  <button onClick={() => moveSection(i, -1)} disabled={i === 0} className="p-1.5 text-kera-muted hover:text-kera-copper disabled:opacity-30" data-testid={`layout-up-${s.key}`}><ChevronUp size={15} /></button>
                  <button onClick={() => moveSection(i, 1)} disabled={i === homeLayout.length - 1} className="p-1.5 text-kera-muted hover:text-kera-copper disabled:opacity-30" data-testid={`layout-down-${s.key}`}><ChevronDown size={15} /></button>
                  <button onClick={() => toggleSection(i)} className="p-1.5 text-kera-muted hover:text-kera-copper" data-testid={`layout-toggle-${s.key}`}>
                    {s.visible ? <Eye size={15} /> : <EyeOff size={15} />}
                  </button>
                </div>
              ))}
            </div>
            <button onClick={saveHomeLayout} className="mt-6 rounded-full bg-black text-white px-6 py-2.5 text-sm hover:bg-kera-gold hover:text-black transition-colors" data-testid="layout-save-button">
              Save Layout
            </button>
          </div>
        )}

        {tab === "journey" && settings && (
          <form onSubmit={saveSettings} className="bg-white border border-kera-line rounded-2xl p-6 sm:p-8 max-w-2xl" data-testid="admin-journey-settings-form">
            <h3 className="font-display font-semibold text-xl text-kera-ink mb-6">Journey Configuration</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Journey Headline">
                <input value={settings.headline} onChange={(e) => setSettings({ ...settings, headline: e.target.value })} className={inputCls} data-testid="settings-field-headline" />
              </Field>
              <Field label="Supporting Text">
                <input value={settings.supporting_text} onChange={(e) => setSettings({ ...settings, supporting_text: e.target.value })} className={inputCls} data-testid="settings-field-supporting-text" />
              </Field>
              <Field label="Origin Name">
                <input value={settings.origin_name} onChange={(e) => setSettings({ ...settings, origin_name: e.target.value })} className={inputCls} data-testid="settings-field-origin-name" />
              </Field>
              <Field label="Origin Country">
                <input value={settings.origin_country} onChange={(e) => setSettings({ ...settings, origin_country: e.target.value })} className={inputCls} data-testid="settings-field-origin-country" />
              </Field>
              <Field label="Origin Label">
                <input value={settings.origin_label} onChange={(e) => setSettings({ ...settings, origin_label: e.target.value })} className={inputCls} data-testid="settings-field-origin-label" />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Origin Latitude">
                  <input type="number" step="any" value={settings.origin_latitude} onChange={(e) => setSettings({ ...settings, origin_latitude: e.target.value })} className={inputCls} data-testid="settings-field-origin-latitude" />
                </Field>
                <Field label="Origin Longitude">
                  <input type="number" step="any" value={settings.origin_longitude} onChange={(e) => setSettings({ ...settings, origin_longitude: e.target.value })} className={inputCls} data-testid="settings-field-origin-longitude" />
                </Field>
              </div>
            </div>
            <div className="mt-5 flex flex-wrap gap-6">
              {[["route_animation", "Route Animation"], ["show_origin", "Display Kerala Origin"], ["show_connections", "Show Hub Connections"]].map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-sm text-kera-muted cursor-pointer">
                  <input type="checkbox" checked={!!settings[key]} onChange={(e) => setSettings({ ...settings, [key]: e.target.checked })} className="accent-[#C5A059] h-4 w-4" data-testid={`settings-toggle-${key.replace(/_/g, "-")}`} />
                  {label}
                </label>
              ))}
            </div>
            <p className="mt-4 text-xs text-kera-muted">Hub sequence is set by each hub's Journey Order value.</p>
            <button type="submit" className="mt-7 rounded-full bg-black text-white px-6 py-2.5 text-sm hover:bg-kera-gold hover:text-black transition-colors" data-testid="settings-save-button">
              Save Settings
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
